import { apiErrorField } from '@agos/api-client'
import {
  draftFromParts,
  extrasCents,
  formatCents,
  hasLiveFiskalDocument,
  ingredientGroups,
  isPizzaProduct,
  parseExtras,
  parseMoneyToCents,
  sizeCmFromName,
  tt,
  type BuilderIngredient,
  type LatLng,
  type PizzaConfig,
  type PizzaDraft,
  type ReopenedPizza,
} from '@agos/shared'

import { tableKeyForOrder } from '../table-map/table-status'

import { extractPizzaSize } from '../kitchen/item-name'

import { lineDisplayName } from './cart'
import type { OrderCartLine } from './cart'
import { manualDiscountCents, type DiscountMode } from './cart-math'
import { coordinatesPatch, loadedPointOf } from './delivery-point'
import { lineNoteOf } from './line-note'
import { extrasField, withParentLinks } from './order-payload'

/**
 * Re-opening an existing order into the builder — `/new-order?edit={id}`.
 *
 * The last unwired piece of the page. `OrderCartLine` already carries `locked`, and `changeQuantity` and
 * `removeLine` already refuse a locked line; nothing ever set the flag, because nothing loaded an order.
 *
 * **The backend has two different writes and they are not interchangeable.** `PATCH /orders/{orderId}`
 * takes both:
 *
 * - `items` — **replaces** every line. `replaceItems` deletes and re-inserts.
 * - `newItems` — **a round.** `addRound`: *"If the kitchen is still working, the new food joins the round
 *   already on the pass — the cook sees one ticket, not two. If the kitchen had finished, it starts a new
 *   round and the order goes back to it."*
 *
 * So sending everything through `items` on a second helping would silently delete the ticket the kitchen
 * is cooking from and re-insert it, which is the wrong instruction to give a busy kitchen. This module
 * splits the cart the way the endpoint expects: lines that were already on the order and are unchanged
 * are left alone, and genuinely new food goes in `newItems`.
 *
 * **What locked means.** Every line carries a `kitchenRound`. Lines below the order's highest round have
 * already been cooked and served from, so they are shown and not edited — the way to remove one is a
 * refund or a storno, not a quiet re-insert. Lines in the highest round are still editable, because that
 * is the round nobody has finished.
 */

export interface LoadedOrder {
  id: number
  orderNumber: string
  status: string
  orderType: string
  customerName: string
  customerPhone: string
  /**
   * Read so it can be diffed - §9.383, FB-681.
   *
   * It was absent from this shape and from the form, so an edit that sent `customerEmail` would have
   * sent an empty one and **cleared the customer's address on every save**. A field is not diffable
   * until it is loaded.
   */
  customerEmail: string
  customerAddress: string
  tableName: string
  notes: string
  /**
   * The delivery fee, in cents - §9.420, FB-747.
   *
   * **Absent from this shape is why the edit screen showed 17,00 on a delivery order.** `OrderFull`
   * carries `deliveryCost`, `discount`, `subtotal` and the three `manualDiscount*` fields; this
   * builder read none of them, so the fee was on the wire and thrown away. And worse than hidden:
   * the save recomputed `total` from the goods alone and **sent** it, and `total` is in the server's
   * `EDITABLE` list.
   */
  deliveryCents: number
  /**
   * Where the order is going - §9.421, FB-748.
   *
   * **The zone is resolved from these, and nothing read them.** `setZoneId` is called from exactly
   * two places, the address box and the autocomplete, so an order *loaded* for editing never
   * resolved a zone at all and the screen said *Zóna nerozpoznaná* about an address it had. An
   * online order always carries these: the customer app will not place a delivery without a
   * resolved address.
   */
  deliveryLat: number | null
  deliveryLon: number | null
  /** Every discount on the order, in cents, as `orders.discount` holds it - manual plus any coupon. */
  discountCents: number
  /**
   * `percent` or `fixed`, as the till recorded it; empty where no manual discount was given.
   *
   * **Corrected in §9.431: this said `absolute`, which the column never holds.** Measured on the
   * owner's data - 154 rows `percent`, 79 `fixed`, and `OrderWriteController` constrains the field
   * to `@Pattern(regexp = "percent|fixed")`. A reader trusting the old comment would write a branch
   * for a value that cannot occur.
   *
   * **And empty is common rather than exceptional**: 1 620 orders carry a discount with no type,
   * because a coupon records none. §9.431 is what that cost.
   */
  manualDiscountType: string
  /** The number that was typed, in the units `manualDiscountType` names - not cents. */
  manualDiscountValue: number
  paymentMethod: string
  /**
   * The online payment's state - §9.374, and it decides one refusal.
   *
   * A method change is refused only when this reads `online_paid`: the money arrived through that
   * method and relabelling it would make the takings lie. A CardPay order that did **not** go
   * through has nothing against it, which is the owner's case.
   */
  paymentStatus: string
  /** Whether the order is a časenka, and for when — legacy reopens the builder on both. */
  isPreorder: boolean
  scheduledAt: string
  /**
   * The register's own two stamps - §9.567, FB-862.
   *
   * `GET /orders/{orderId}` has answered both since the column existed and this reader took neither,
   * which is §3.122's shape. Together they say whether a **live** fiscal document exists: printed and
   * not since cancelled. The order editor refuses a money edit on one, because §10A.292 refuses it on
   * the server and discovering that at the end of a filled-in form is §9.155's late refusal.
   */
  fiskalPrintedAt: string | null
  fiskalStornoAt: string | null
  lines: OrderCartLine[]
  maxRound: number
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 1
}

/** `GET /orders/{id}` into the shape the builder holds, with the earlier rounds locked. */
/**
 * A `numeric` column as cents - §9.420.
 *
 * `number | string` on purpose: these are `numeric` columns and this API has handed them over as
 * strings before (`agos-a-jsonb-column-publishes-the-drivers-wrapper`). Anything unreadable answers
 * 0, which means *no fee* and is the safe direction for a figure about to be diffed.
 */
function money(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 100)
  if (typeof value === 'string' && value.trim() !== '') return parseMoneyToCents(value)
  return 0
}

export function loadedOrderOf(payload: unknown): LoadedOrder | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (typeof row.id !== 'number') return null

  const items = Array.isArray(row.items) ? row.items : []
  const rounds = items.map((entry) => {
    const round = (entry as Record<string, unknown>).kitchenRound
    return typeof round === 'number' ? round : 1
  })
  const maxRound = rounds.length === 0 ? 1 : Math.max(...rounds)
  /* §9.713 - which rows something was ordered with, so the parent gets a `lineId` its add-ons can name. Only those:
     a `lineId` on every row would change every loaded line for an order with no add-ons at all. */
  const rowIds = new Set(
    items.map((entry) => (entry as Record<string, unknown>).id).filter((id): id is number => typeof id === 'number'),
  )
  const parentRows = new Set(
    items
      .map((entry) => addonParentRowOf(entry))
      .filter((id): id is number => id !== null && rowIds.has(id)),
  )

  return {
    id: row.id,
    orderNumber: text(row.orderNumber),
    status: text(row.status),
    orderType: text(row.type),
    customerName: text(row.customerName),
    customerPhone: text(row.customerPhone),
    customerEmail: text(row.customerEmail),
    customerAddress: text(row.customerAddress),
    fiskalPrintedAt: typeof row.fiskalPrintedAt === 'string' ? row.fiskalPrintedAt : null,
    fiskalStornoAt: typeof row.fiskalStornoAt === 'string' ? row.fiskalStornoAt : null,
    // §9.202 — `row.tableName` is a field the server does not send: `orders` has no `table_name`
    // column and the orders API no such property. A table order is identified by its
    // `customerName` — *Stôl 5* — which is what `tableKeyForOrder` reads for the board, the map and
    // the dispatch list, and what `buildOrderPayload` says it writes.
    //
    // Reading the absent field wiped the number the *Otvorené stoly* link had already put in the
    // box, and `submitProblems` then refused the save with *Zadajte číslo stola*.
    tableName: tableKeyForOrder(row) ?? '',
    notes: text(row.notes),
    // §9.420 - euros on the wire, cents here.
    deliveryCents: money(row.deliveryCost),
    deliveryLat: typeof row.deliveryLat === 'number' ? row.deliveryLat : null,
    deliveryLon: typeof row.deliveryLon === 'number' ? row.deliveryLon : null,
    discountCents: money(row.discount),
    manualDiscountType: text(row.manualDiscountType),
    manualDiscountValue: money(row.manualDiscountValue) / 100,
    paymentMethod: text(row.paymentMethod) === '' ? 'cash' : text(row.paymentMethod),
    paymentStatus: text(row.paymentStatus),
    isPreorder: row.isPreorder === true,
    scheduledAt: text(row.scheduledAt),
    maxRound,
    lines: items.map((entry) => {
      const item = entry as Record<string, unknown>
      const round = typeof item.kitchenRound === 'number' ? item.kitchenRound : 1
      return {
        productId: typeof item.productId === 'number' ? item.productId : 0,
        // §9.730, owner O100 - a saved menu pizza is a pizza again: `isPizza`, and its toppings bracket moved out
        // of the name into `pizzaExtras`, which `lineDisplayName` puts back byte for byte on the save.
        ...loadedPizzaFields(item),
        unitPrice: item.unitPrice as number,
        quantity: count(item.quantity),
        ...(text(item.categoryName) === '' ? {} : { categoryName: text(item.categoryName) }),
        ...(text(item.boxGroupId) === '' ? {} : { boxGroupId: text(item.boxGroupId) }),
        ...(text(item.boxGroupName) === '' ? {} : { boxGroupName: text(item.boxGroupName) }),
        ...(text(item.boxSlotLabel) === '' ? {} : { boxSlotLabel: text(item.boxSlotLabel) }),
        // Everything below the highest round has been cooked from.
        ...(round < maxRound ? { locked: true } : {}),
        // Carried so the split below can tell an existing line from a new one.
        existingId: typeof item.id === 'number' ? item.id : undefined,
        // §9.697 - an amount gift stays one on an edit, so the builder neither offers it twice nor keeps it once the
        // edit takes the order under the amount. A product gift is loaded as it always was: re-pricing it from its
        // rule on every edit would be a change of its own.
        ...(text(item.giftRuleType) === 'amount' && typeof item.giftRuleId === 'number'
          ? { giftRuleId: item.giftRuleId, giftRuleType: 'amount' }
          : {}),
        /**
         * §10A.202 - what a composed line was assembled from, and **deliberately not `parts`**.
         *
         * `itemOf` sends `parts` when a cart line has them, and `boundedByParts` then bounds the
         * line on the **edit** path - which it does not do today and which is `BLOCKED #12`'s open
         * half, open because a previous session built it and reverted it: *"re-pricing an edit
         * would overwrite a correction a manager made deliberately."* So this is a separate field
         * the payload builder does not read, and it stays separate until that question is answered.
         */
        ...(composedPartsOf(item.parts).length === 0
          ? {}
          : { composedParts: composedPartsOf(item.parts) }),
        /**
         * §9.690, owner #3 - the line's own note, read so the box shows it and so the save can tell
         * a changed note from an untouched one. Read off the untyped row because the generated
         * `OrderLine` does not declare it until the client is regenerated; absent and `null` are
         * both *no note*.
         */
        ...(lineNoteOf(item) === undefined ? {} : { note: lineNoteOf(item) }),
        // §9.723 - the course a day's-menu line is (`BACKEND.md` §10A.88), read so `itemOf` can send it back: `items`
        // re-inserts every line from the wire, and a line re-sent without it is boxed by nothing in the kitchen and
        // counted as *Vlastné a kombo* in Sumáre. Absent on every other line.
        ...(text(item.lunchMealType) === '' ? {} : { lunchMealType: text(item.lunchMealType) }),
        ...upsellLinkOf(item, parentRows),
      }
    }),
  }
}

/**
 * The row an order line was ordered with - `addonOfId`, §9.713 / `BACKEND.md` §10A.366 - or null.
 *
 * Read off the untyped row because the generated `OrderFullLine` does not declare it until the client is regenerated.
 */
export function addonParentRowOf(row: unknown): number | null {
  if (typeof row !== 'object' || row === null) return null
  const value = (row as Record<string, unknown>).addonOfId
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null
}

/** The `lineId` a loaded row is given when something on the order was ordered with it - §9.713. */
export function loadedLineId(rowId: number): string {
  return `row:${rowId}`
}

/**
 * §9.713 - a loaded line's place in the parent link: the parent gets `lineId`, an add-on `addonOf` (its parent's) and
 * `upsellGroupId`, so the cart draws it under its parent and an edit that rewrites `items` sends the link again -
 * `items` deletes and re-inserts every line, so a link left out of it would be a link the save erases, and the
 * server's re-price would charge a free dip at its catalogue price. **A link to a row this order does not hold is
 * dropped**: the cart cannot draw it and the payload could not name it.
 */
function upsellLinkOf(
  item: Record<string, unknown>,
  parentRows: ReadonlySet<number>,
): Pick<OrderCartLine, 'lineId' | 'addonOf' | 'upsellGroupId'> {
  const own = typeof item.id === 'number' && parentRows.has(item.id) ? { lineId: loadedLineId(item.id) } : {}
  const parent = addonParentRowOf(item)
  if (parent === null || !parentRows.has(parent)) return own
  const group = item.upsellGroupId
  return {
    ...own,
    addonOf: loadedLineId(parent),
    ...(typeof group === 'number' && Number.isInteger(group) ? { upsellGroupId: group } : {}),
  }
}

/**
 * A saved line's name and its pizza fields - `FRONTEND.md` §9.730, owner O100, FB-1031.
 *
 * **The owner** (Sereď): *"Keď vytvorím obj s pizzou a uložím obj a chcem upraviť v obj pizzu nedá sa"*. `OrderCart`
 * draws *Upraviť prísady* on `isPizza` alone and this reader set neither it nor `pizzaExtras`, so a menu pizza off a
 * saved order could not take a topping, a garlic crust or (§9.731) another base. Legacy is the same - its loader
 * sets neither - so this goes beyond parity because the owner asked for it.
 *
 * - **A pizza by `isPizzaProduct`** - the line's own `productType` (`BACKEND.md` §10A.378 answers it beside the
 *   category) or its category's name, the rule the till adds a pizza by. A composed line is not one here: it reopens
 *   in the builder (§9.465). A legacy size-variant id (`52001`) has no product row and answers neither, which is
 *   right twice over - an edit could not save it anyway, because the server re-prices through `products`.
 * - **The bracket moves out of the name** into `pizzaExtras` - only when {@link extrasBracketOf} can put it back byte
 *   for byte, so the save's `customName` is the stored name. A bracket it cannot move leaves the line as it was and
 *   **not a pizza**: offered the dialog it would come back as `X [a] [b]`.
 */
export function loadedPizzaFields(
  item: Record<string, unknown>,
): Pick<OrderCartLine, 'productName' | 'isPizza' | 'pizzaExtras'> {
  const name = text(item.productName)
  const productId = typeof item.productId === 'number' ? item.productId : 0
  const pizza =
    !hasNoCatalogueRow(productId) &&
    isPizzaProduct({ productType: text(item.productType), categoryName: text(item.categoryName) })
  if (!pizza) return { productName: name }
  if (!name.includes('[')) return { productName: name, isPizza: true }
  const bracket = extrasBracketOf(name)
  return bracket === null
    ? { productName: name }
    : { productName: bracket.head, isPizza: true, pizzaExtras: bracket.extras }
}

/**
 * A stored name split into the product's own and the bracket the extras dialog wrote, or null - §9.730.
 *
 * `OrderCreationService.extrasSuffix` stores the bracket last, after one space: `Margherita – 32 cm [+Šunka]`. The
 * split is accepted only when {@link lineDisplayName} puts it back **byte for byte**, because that is what the save
 * sends as `customName` and the kitchen, the receipt and the server's round matching all read the stored name.
 */
export function extrasBracketOf(name: string): { head: string; extras: string } | null {
  const open = name.indexOf('[')
  if (open <= 0 || !name.endsWith(']')) return null
  const head = name.slice(0, open).trimEnd()
  const extras = name.slice(open + 1, -1).trim()
  if (head === '' || extras === '') return null
  return lineDisplayName({ productName: head, pizzaExtras: extras }) === name ? { head, extras } : null
}

/**
 * A saved pizza's toppings, read back out of its bracket once the ingredients have answered - §9.730.
 *
 * **What the line was charged for and how much of its price that is.** `extrasChosen` goes out as the line's
 * `extras` (`itemOf`), so the server prices the save from what it says rather than recovering it from the name
 * (`BACKEND.md` §10A.378 keeps that recovery for a client that does not say); `extrasPriceCents` is what
 * `setLineExtras` takes off before adding a new choice, without which a topping changed on a saved pizza would be
 * charged on top of the one it replaced. Read against the dialog's own list - `ingredientGroups(ingredients)` - with
 * `parseExtras`, so the line and the dialog it opens agree; priced at the line's size, as the dialog prices.
 *
 * **Idempotent and cheap**: only a saved pizza line carrying a bracket and neither field is touched, and the same
 * array comes back when nothing was - the page applies it while rendering (§9.697's pattern), so it settles in one
 * pass. A bracket that names nothing on offer (only a base, or toppings since withdrawn) is left alone; the server's
 * recovery answers for it.
 */
export function withLoadedExtras(
  cart: OrderCartLine[],
  ingredients: readonly BuilderIngredient[],
): OrderCartLine[] {
  const offered = ingredientGroups(ingredients).flatMap((group) => group.ingredients)
  if (offered.length === 0) return cart
  let changed = false
  const next = cart.map((line) => {
    if (line.existingId === undefined || line.isPizza !== true || !line.pizzaExtras) return line
    if (line.extrasChosen !== undefined || line.extrasPriceCents !== undefined) return line
    const chosen = parseExtras(line.pizzaExtras, offered)
    if (chosen.size === 0) return line
    changed = true
    return {
      ...line,
      extrasChosen: [...chosen].map(([ingredientId, quantity]) => ({ ingredientId, quantity })),
      extrasPriceCents: extrasCents(chosen, offered, sizeCmFromName(line.productName) ?? 32),
    }
  })
  return changed ? next : cart
}

/**
 * Said above the earlier rounds, so what changing one *does* is on the screen - §9.374, FB-668.
 *
 * **It used to say they could not be changed**, and the owner asked for exactly that: *"Add
 * posibility to edit previous round… they ordered 3x čapovaná kofola, but when you start čapovať
 * them, they will tell you: «We want only two, not three»."* So the note stops refusing and starts
 * explaining: a change to a line that is already on the order **rewrites the order** rather than
 * adding a round, so the kitchen sees nothing new for it. That is legacy's behaviour and the one
 * thing an operator cannot guess from the screen.
 */
export function lockedNote(order: LoadedOrder): string {
  const locked = order.lines.filter((line) => line.locked === true).length
  if (locked === 0) return ''
  return `${tt('Skoršie kolá')} (${locked}) ${tt('sa dajú opraviť - zmena ich prepíše bez nového kola pre kuchyňu. Doobjednávka je vždy nové kolo.')}`
}

/** One piece of a composed line, as `GET /orders/{id}` answers it - §10A.202. */
export interface ComposedPart {
  name: string
  quantity: number
}

/**
 * A composed line's pieces, off the wire - §10A.202, FB-748.
 *
 * Kept to the two fields a reader needs. The price is on the wire too and is deliberately not read
 * here: showing an ingredient's own surcharge beside the line's total invites the arithmetic
 * question *"why does 6,50 + 1,40 + 1,60 not equal 9,50"*, whose answer is §10A.14's bound and not
 * anything an operator is asking.
 */
export function composedPartsOf(raw: unknown): ComposedPart[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const part = entry as Record<string, unknown>
    const name = text(part.name)
    if (name === '') return []
    const quantity = typeof part.quantity === 'number' && part.quantity > 1 ? part.quantity : 1
    return [{ name, quantity }]
  })
}

/**
 * What a composed line is made of, in one line - §10A.202, FB-748.
 *
 * **The owner:** *"when we have ccustom pizza ordered online, and they want to edit pizza, we cant
 * edit it, its like normal položka and not editable pizza"*. This is the half of that which is
 * answerable without a pricing decision: the line stops reading as a plain item and says what the
 * pizza is, in the order it was composed - the size, then the base, then the toppings, which is the
 * sequence `pizzaParts` emits and `order_item_parts.position` preserves.
 *
 * A portion count is shown only when it is more than one, because *Šunka ×1* is noise on every line.
 */
export function compositionOf(line: { composedParts?: readonly ComposedPart[] }): string {
  const parts = line.composedParts ?? []
  if (parts.length === 0) return ''
  return parts
    .map((part) => (part.quantity > 1 ? `${part.name} x${part.quantity}` : part.name))
    .join(' · ')
}

/**
 * Whether a line names no catalogue row, which is what makes it composed - §9.465.
 *
 * **Zero is not the only spelling and that was a real hole.** `composedLine` derives a **negative**
 * synthetic id from the line's own name and the manager's payload builder flattens the same thing to
 * **0** - `agos-two-payload-builders-differ` is about exactly that divergence. Both mean *there is no
 * product row behind this*.
 *
 * **Measured on the owner's data**, because the split is not close: of the custom-pizza and kombo
 * lines, **454 carry a negative id and 45 carry zero**; across every line, 4 100 negative to 488 zero.
 * §9.440's note read `productId === 0` and so fired on 45 of 499 - and never on the case the owner
 * reported, which is a pizza ordered **online**, through the customer app, which sends the negative
 * id. A comment in this file recorded the 488 as *"the"* composed lines placed before `V24`;
 * `agos-the-only-one-needs-its-scope-written`.
 */
function hasNoCatalogueRow(productId: number | undefined): boolean {
  return (productId ?? 0) <= 0
}

/**
 * Said on a composed line whose pieces were never recorded - §10A.202.
 *
 * **Not every composed line has them, and the reason is a date.** `order_item_parts` arrived with
 * `V24`; every composed line placed before it has nothing stored, so there is nothing to show.
 * Saying so is the honest answer - the alternative is a control that silently rebuilds a pizza from
 * its name, and §9.465 makes that one refuse rather than guess.
 *
 * **The measurement, corrected.** This paragraph read *"the 488 `product_id = 0` lines"*, which is one
 * of two spellings: `hasNoCatalogueRow` says why, and on the owner's data the other spelling is **454
 * of 499** custom pizzas. So this note fired on 45 of them and never on the online order the report is
 * about.
 */
export function compositionMissingNote(line: {
  productId?: number
  existingId?: number
  composedParts?: readonly ComposedPart[]
}): string {
  // **`existingId` and not `productId === 0` alone**, which is what the first version of this said
  // and it was wrong on the screen it is drawn on. A custom pizza *being built* has `productId: 0`
  // too - it has no catalogue row either - so the note fired while the operator was still choosing
  // toppings and told them the composition had not been stored. Only a line that came off an
  // existing order can be missing pieces, and `existingId` is the only field that says so:
  // `loadedOrderOf` sets it and nothing in the add path does.
  if (line.existingId === undefined) return ''
  if (!hasNoCatalogueRow(line.productId)) return ''
  if ((line.composedParts ?? []).length > 0) return ''
  return tt('Zloženie tejto položky sa neukladalo - je len v jej názve.')
}

/**
 * Whether a cart line is a custom pizza that can go back into the builder - §9.465, §9.703.
 *
 * **A line off a saved order - four conditions, and each of them earns its place.**
 *
 * - `existingId` - the line came off a saved order. §9.440's note records why that is the field which
 *   says so, after getting it wrong once.
 * - `productId === 0` - no catalogue row. That is what a custom pizza is.
 * - parts recorded - a line placed before `V24` has none, so there is nothing to rebuild;
 *   {@link compositionMissingNote} is what the screen says about those instead.
 * - not a kombo - its parts are three whole pizzas with catalogue ids and its own builder, so
 *   reopening one in the single-pizza builder would produce a pizza made of pizzas.
 *
 * **Or a pizza built in this košík - §9.703, FB-1009.** This used to say a pizza without `existingId` *"is
 * already in the builder"*, and once *Pridať pizzu do košíka* is pressed it is not: the add resets the builder,
 * so a pizza added a minute ago could only be deleted and built again from nothing - the operator's *"not
 * working in nová objednávka when editing pizza in košík"*. Such a line carries the draft it was built from
 * (`OrderCartLine.pizzaDraft`), which is the whole pizza, so it needs neither an `existingId` nor parts. It
 * still has no catalogue row and is still not a kombo.
 */
export function reopenablePizza(line: {
  productId?: number
  existingId?: number
  composition?: string
  composedParts?: readonly ComposedPart[]
  pizzaDraft?: PizzaDraft
}): boolean {
  if (!hasNoCatalogueRow(line.productId)) return false
  if (line.composition === 'kombo') return false
  if (line.pizzaDraft !== undefined) return true
  if (line.existingId === undefined) return false
  return (line.composedParts ?? []).length > 0
}

/**
 * The draft a reopened custom pizza goes back into the builder with - §9.465, §9.703.
 *
 * **A pizza built in this košík comes back as itself**: its own draft, every portion count and the name it
 * was given, with no name match that could refuse it - a 3× stays 3×. **A pizza off a saved order** is
 * rebuilt from its stored parts by `draftFromParts`, which refuses rather than opening it wrong, and takes
 * the line's own name as its required fourth argument (§9.680).
 */
export function reopenedDraft(
  line: Pick<OrderCartLine, 'pizzaDraft' | 'composedParts' | 'productName'>,
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[],
): ReopenedPizza {
  if (line.pizzaDraft !== undefined) return { draft: line.pizzaDraft, refusal: '' }
  return draftFromParts(line.composedParts ?? [], config, ingredients, line.productName)
}

/** One cart line as the endpoint wants it, for either `newItems` or `items`. */
function itemOf(line: OrderCartLine & { existingId?: number }): Record<string, unknown> {
  return {
      productId: line.productId,
      productName: line.productName,
      // §9.420 - **the field this builder never sent.** `customName` is where a line's topping text
      // lives and what the kitchen ticket reads, so rewriting `items` without it erased what the
      // customer asked for: FB-747's *"note were missing after edit"*. Shared with the create path
      // rather than copied, because these two builders have now diverged twice.
      customName: lineDisplayName(line),
      quantity: line.quantity,
      unitPrice: parseMoneyToCents(line.unitPrice) / 100,
      ...(line.boxGroupId === undefined ? {} : { boxGroupId: line.boxGroupId }),
      ...(line.boxGroupName === undefined ? {} : { boxGroupName: line.boxGroupName }),
      ...(line.boxSlotLabel === undefined ? {} : { boxSlotLabel: line.boxSlotLabel }),
      // §10A.14 and §10A.26. Without these a composed line arriving as a round has nothing to be
      // bounded against: `boundedByParts` with no parts returns the price it was sent, so a kombo
      // added to a table that has already ordered was priced entirely by the client. Measured at
      // 99,00 € on the running stack before this line existed.
      ...(line.parts && line.parts.length > 0
        ? {
            parts: line.parts.map((part) => ({
              ...(part.productId === undefined ? {} : { productId: part.productId }),
              name: part.name,
              unitPrice: part.unitPriceCents / 100,
              quantity: part.quantity,
            })),
          }
        : {}),
      ...(line.composition ? { composition: line.composition } : {}),
      // §10A.34 — an edit re-prices, and a re-price that does not know the size charges the base.
      // Measured before this was sent: a 50 cm pizza came back from an edit at the 32 cm price,
      // under a name that had lost the size.
      //
      // §9.506, FB-796 - **through `recoveredSize`, because on a loaded line this was always
      // `undefined`** and the spread above therefore sent nothing for eight weeks. The comment two
      // lines up describes the defect it was written to fix and it has been live the whole time.
      ...(recoveredSize(line) === undefined ? {} : { size: recoveredSize(line) }),
      // §9.730, `BACKEND.md` §10A.378, FB-1032 - **the toppings, which this never sent.** The server re-prices every
      // line it is handed and priced a menu pizza bare, so an edit took the ham off the bill while the name kept it.
      // The shared expression, so the edit cannot drift from creation on the field that charges.
      ...extrasField(line),
      // §9.690, owner #3 - `items` deletes and re-inserts every line, so a note left out here is a
      // note the save erases. Absent when the line has none, which is also how a cleared one is said.
      ...(lineNoteOf(line) === undefined ? {} : { note: lineNoteOf(line) }),
      // §9.723 - the same reason as the note: a lunch line re-sent without its course loses it.
      ...(line.lunchMealType === undefined ? {} : { lunchMealType: line.lunchMealType }),
  }
}

/**
 * The existing lines whose note changed, addressed by `order_items.id` - §9.690, `BACKEND.md`
 * §10A.349.
 *
 * **A note is a change and it was invisible to this file.** `editPayload` decided *did anything that
 * was already on the order move* by comparing quantities, so a note typed onto a loaded line would
 * have been read as *nothing changed* and dropped.
 *
 * `null` for a note that was removed, because the server reads that as *clear it* and an absent key
 * would be refused as an entry that says nothing.
 */
export function lineNoteChanges(
  cart: readonly (OrderCartLine & { existingId?: number })[],
  loaded?: LoadedOrder | null,
): { id: number; note: string | null }[] {
  const before = new Map<number, string | undefined>()
  for (const line of loaded?.lines ?? []) {
    const id = (line as OrderCartLine & { existingId?: number }).existingId
    if (id !== undefined) before.set(id, lineNoteOf(line))
  }
  return cart.flatMap((line) => {
    if (line.existingId === undefined || line.quantity <= 0 || !before.has(line.existingId)) {
      return []
    }
    const now = lineNoteOf(line)
    return now === before.get(line.existingId) ? [] : [{ id: line.existingId, note: now ?? null }]
  })
}

/**
 * The existing lines whose name changed, by `order_items.id` - §9.730, owner O100.
 *
 * **A saved pizza edited in the dialog keeps its quantity**, and `editPayload` decided *did anything already on the
 * order move* by comparing quantities alone - so new toppings, a garlic crust or another base on a saved pizza would
 * have saved as *Nič sa nezmenilo*. The name is what the dialog changes (`lineDisplayName`, the stored name the
 * server keeps), so a line whose name is not the one it was loaded with is a correction, and only `items` carries
 * one. A line stepped to nought is the quantity rule's, not this one's.
 */
export function renamedExistingIds(
  cart: readonly (OrderCartLine & { existingId?: number })[],
  loaded?: LoadedOrder | null,
): Set<number> {
  const before = new Map<number, string>()
  for (const line of loaded?.lines ?? []) {
    const id = (line as OrderCartLine & { existingId?: number }).existingId
    if (id !== undefined) before.set(id, lineDisplayName(line))
  }
  const renamed = new Set<number>()
  for (const line of cart) {
    if (line.existingId === undefined || line.quantity <= 0) continue
    const was = before.get(line.existingId)
    if (was !== undefined && lineDisplayName(line) !== was) renamed.add(line.existingId)
  }
  return renamed
}

/**
 * The size this line should be priced at - §9.506, FB-796.
 *
 * **The line's own value wins.** A line the operator added in this session carries the size they
 * picked, and re-deriving it from text would be a second opinion on a fact that is not in doubt.
 *
 * **Otherwise it is read off the stored name, which is the only place it exists.** `order_items` has
 * no size column and the order detail answers none, so `fromOrderRow` cannot set one - and
 * `withSize` is what put `– 50 cm` into the name, so the name is a faithful record of it.
 * `extractPizzaSize` already does this parse for the kitchen card and is reused rather than copied.
 *
 * **Only for a line with a catalogue row**, and this is the one rule here worth reading twice.
 * `ServerPricing` prices by label *only* for `productId > 0`; a custom pizza or a kombo is priced by
 * its parts and its product has no `pizza_sizes` at all, so handing one a label would ask the server
 * to match against an empty map - and its answer to an unmatched label is a **refusal**. Sending it
 * would turn every edit of a custom pizza into an error, which is worse than the defect.
 */
export function recoveredSize(line: {
  productId?: number
  size?: string
  productName?: string
}): string | undefined {
  if (line.size !== undefined) return line.size
  if (hasNoCatalogueRow(line.productId)) return undefined
  return extractPizzaSize(line.productName ?? '') ?? undefined
}

/**
 * What an edit sends - §9.374, FB-668, and it used to be able to send only one of these three.
 *
 * **The old version returned `{newItems}` or `null`**, where `newItems` was *lines with no
 * `existingId`*. Four things an operator can do on that screen therefore reached the server as
 * nothing at all, and the owner reported all four in one batch:
 *
 * - **adding a product the order already has.** `addProduct` merged into the existing line, so the
 *   quantity rose on a line carrying an `existingId` and the payload came out empty: *"Zatiaľ nič
 *   nepridané"*, and a guest drank a kofola nobody was charged for. (`addProduct` no longer merges
 *   into a loaded line, so this is now an added line as well - but the rest of this function is what
 *   makes the other three possible.)
 * - **correcting three kofolas to two**, the owner's own example, while they are being poured.
 * - **removing a line.**
 * - **switching a failed CardPay order to *Karta kuriér***, which could not be sent at all.
 *
 * **Legacy has three branches and its comments are the specification** (`restaurant.tsx:847`):
 * replace the whole array when a line that was already there changed - *"covers removing a beef bowl
 * that came in damaged, decrementing a previous-round drink"* - send only the new lines when nothing
 * else moved, so the kitchen gets a round, and send the scalars alone when neither happened.
 *
 * **`items` rewrites the order and starts no round**, which is the trade legacy names out loud and
 * the reason the three branches are ordered this way: a correction is not a round, and food that is
 * genuinely new is.
 */
export interface EditScalars {
  notes?: string
  paymentMethod?: string
  /**
   * Cleared alongside a method change on an order whose online payment never landed.
   *
   * **`null` and nothing else - FB-847, §10A.282.** `OrderEditController.EDITABLE` did not carry this name until
   * that entry, so the clear was silently dropped and the board's red badge stayed on an order paid at the door.
   * The server now accepts it **as a clear only** and refuses a value by name, because accepting one would let a
   * client write `online_paid` and mark an unpaid order paid.
   */
  paymentStatus?: null
  discount?: number
  /** §9.420 - the fee, so the total and its parts cannot disagree after an edit. */
  deliveryCost?: number
  /**
   * The manual discount's definition - `null` for both is a clear (§9.744 amended): a box the operator emptied takes
   * its type and value with it, as *Zaplatiť*'s clear does (`editedDiscountPayload`), so a percentage taken off cannot
   * be seeded back on the next edit.
   */
  manualDiscountType?: string | null
  manualDiscountValue?: number | null
  manualDiscountOperator?: string | null
  /**
   * Everything else the form can change and the save could not send - §9.383, FB-681.
   *
   * The owner asked for one of them (*"changing objednávka from reštaurácia to osobný odber - its
   * not possible"*) and every field here had the same defect: the control worked, the save dropped
   * it, and the screen said *Objednávka uložená*. `OrderEditController.EDITABLE` has taken all of
   * them since it was written.
   */
  type?: string
  customerName?: string
  customerPhone?: string
  customerEmail?: string
  customerAddress?: string
  /**
   * Where the delivery goes - §9.693, FB-1003. Both or neither, and `null` for both clears them.
   *
   * `OrderEditController.EDITABLE` has taken them all along and `OrderUpdateService` lets them change after
   * a print, because a corrected door is not a corrected receipt. The editor never sent them, so an address
   * corrected on an edit kept the old address's point - and the driver's *Navigovať* goes to the point.
   */
  deliveryLat?: number | null
  deliveryLon?: number | null
  employeeId?: number
  isPreorder?: boolean
  scheduledAt?: string | null
}

/** The form as it stands, for the diff below. Resolved values, not the controls. */
export interface EditFields {
  /** The channel's slug, as the chips set it. */
  type: string
  /** Whether that channel seats people - its capability, never its slug (§10A.87). */
  seatsGuests: boolean
  /** `Stôl 5` for a seating channel; ignored otherwise. */
  tableName: string
  customerName: string
  customerPhone: string
  customerEmail: string
  customerAddress: string
  /**
   * Where the delivery goes, as the builder holds it - §9.693.
   *
   * **Optional, and absent means *say nothing about it*** - so a caller that does not pass it sends no
   * coordinates, exactly as before. `null` means the builder holds no point, which on an order that had one
   * is a clear: the address was changed without one.
   */
  deliveryPoint?: LatLng | null
  notes: string
  /**
   * The discount the cart has computed, in cents - §9.420.
   *
   * **`OrderCart` has rendered a live *Zľava* control all along and `NewOrderPage` never passed it
   * here**, so `editScalars` could not set `scalars.discount` however it was written, and the screen
   * still said *Objednávka uložená*. §9.383 fixed this exact shape for eight other fields and left
   * a comment saying so; the discount is the ninth.
   *
   * §9.744 amended - while the box is untouched the cart's manual part is {@link untouchedDiscountCents}, the figure
   * the server will store, so this is that plus any coupon applied in this edit.
   */
  discountCents: number
  /**
   * How the manual half was expressed, so the order records it the way the create path does.
   *
   * `DiscountMode` rather than `string`: the first version of this typed it loosely and the seeding
   * code then wrote `'absolute'`, which is not a member - the till's two modes are `percent` and
   * `fixed`. Only `tsc` objected. A union that names its members cannot be guessed wrong.
   */
  discountMode: DiscountMode
  /** The number typed into the box, in `discountMode`'s units. `''` where none was. */
  discountValue: string
  /** Who gave it. A discount nobody is named for is a discount nobody can be asked about. */
  operatorName: string | null
  /**
   * Whether the operator touched the *Zľava* box in this edit - §9.744 amended, owner O114, `BACKEND.md` §10A.397
   * amended.
   *
   * False until somebody types in the box, switches its unit or clears it, and **always false while the box is
   * hidden** (no `order_discount`). Untouched, {@link editScalars} says nothing about the manual discount and the
   * server works the figure out from the lines - which is the only answer a role without the discount right can be
   * given, because every figure the editor sent of its own was read as a new manual discount. Required - §9.480's
   * rule, an optional flag is dropped at a call site with nothing to say so.
   */
  discountTouched: boolean
  /**
   * A coupon applied in this edit, in cents - nought when none - §9.744 amended.
   *
   * This endpoint takes no coupon code, so a coupon applied in the editor reaches the order only as the discount
   * figure. Untouched, it is the one thing about the discount the save still says - the figure alone, which the
   * server asks either discount right for, and the coupon box is drawn only for `apply_coupon`. Without it a coupon
   * the cart had already subtracted would be dropped by the save with nothing said.
   */
  couponCents: number
  /** The delivery fee the form resolved, in cents - the zone's or the dispatcher's own figure. */
  deliveryCents: number
  /** The whole payable figure the cart shows, in cents. Sent rather than recomputed here. */
  totalCents: number
  paymentMethod: string
  /** Only sent where the channel seats people, exactly as the create path decides it. */
  waiterId: number | null
  isPreorder: boolean
  /** ISO, or `''`/null where the form holds no time - `scheduledAtOf` answers null. */
  scheduledAt: string | null
}

/**
 * What an edit changes, beyond the lines - §9.383, FB-681.
 *
 * **Only what differs from the order as it was loaded.** A patch that names a field rewrites it, so
 * re-sending an unchanged note would stamp an edit on an order nobody edited - and `PATCH
 * /orders/{id}` distinguishes *absent* from *null* precisely so a caller can say nothing about a
 * field.
 *
 * **The name follows the channel, not the slug.** A channel that seats people carries `Stôl N` as
 * its `customerName` - that is how the board, the map and the dispatch list identify a table
 * (§9.202) - and one that does not carries the person's own name. So switching *Reštaurácia* to
 * *Osobný odber* has to move the name across with it, or the order stays called *Stôl 5* on a
 * channel with no tables.
 */
/**
 * Whether two timestamps name the same minute - §9.383.
 *
 * **A string comparison would have re-sent the time on every save.** The form holds the časenka as a
 * date and a time, so saving recomposes it through `toISOString()` - `…T17:30:00.000Z` - while the
 * order was loaded with whatever the server serialised. Equal instants, different strings, and a
 * patch that names a field rewrites it. Minute precision because that is what the picker offers.
 */
function sameMinute(left: string, right: string): boolean {
  const first = Date.parse(left)
  const second = Date.parse(right)
  if (Number.isNaN(first) || Number.isNaN(second)) return left === right
  return Math.floor(first / 60_000) === Math.floor(second / 60_000)
}

/**
 * What the order is called after this edit - §9.383, and the second half of the owner's report.
 *
 * **A table order is identified by its name.** `Stôl 5` in `customerName` is what *Otvorené stoly*,
 * the table map and the dispatch list all read (§9.202), and the form loads that name into the
 * customer box. So switching the channel to *Osobný odber* and leaving the box alone would send the
 * new channel with the old table name - and the owner said what that looks like: *"objednávka
 * zostáva stále v otvorené stoly, nech ju zmením akokoľvek"*.
 *
 * Three cases, and the middle one is the one that was missing:
 *
 * <ul>
 *   <li>the channel seats people: the name is composed from the table box, as the create path does
 *   <li>it no longer does, and the box still holds the table name nobody typed: **cleared**, because
 *       `Stôl 5` was never a person and a pickup with no name is ordinary
 *   <li>it no longer does, and somebody has typed a name: that name, untouched
 * </ul>
 */
function nameFor(loaded: LoadedOrder, current: EditFields): string {
  if (current.seatsGuests) {
    return current.tableName.trim() === ''
      ? loaded.customerName
      : `${tt('Stôl')} ${current.tableName.trim()}`
  }
  const wasATable = loaded.tableName !== ''
  return wasATable && current.customerName === loaded.customerName ? '' : current.customerName
}

/**
 * What the *Zľava* box opens on when an order is edited - §9.420, §9.431, and §9.744's reason for it being a rule.
 *
 * Seeded from `manualDiscountType`/`Value`, which is how the till recorded it, so a percentage typed as 10 comes back
 * as 10 % and not as the euros it happened to work out to; and from the whole `discountCents`, in euros, where no
 * type was recorded - a coupon records none, nor do the rows that predate the column (1 620 of the owner's orders),
 * and a box that opened empty on those made the diff read *the discount was removed* on any save.
 *
 * **§9.744, owner O114 - and the same seed whether or not the box is drawn**, which is what this having no right in
 * its signature says. Without `order_discount` the box is hidden (`discountBoxesFor`), and hidden must mean *left as
 * the order had it*: `editScalars` compares the cart's discount with the loaded one, so a box emptied rather than
 * hidden would send a discount of nought and clear a discount the operator was not allowed to touch. `null` for an
 * order with no discount, which leaves the box as a new order has it.
 *
 * **§9.744 amended - the definition only where its discount is in force** (`discountCents > 0`). A percentage left on
 * the row by a clear that sent only `discount: 0` used to come back on the next edit: seeded as 10 %, the cart worked
 * out a discount the order no longer had and the save sent it as a new one - refused for a role without the right,
 * and given away to a customer by one with it.
 */
export function seededDiscountOf(loaded: LoadedOrder): { mode: DiscountMode; value: string } | null {
  if (loaded.manualDiscountType !== '' && loaded.discountCents > 0) {
    return {
      mode: loaded.manualDiscountType === 'percent' ? 'percent' : 'fixed',
      value: String(loaded.manualDiscountValue),
    }
  }
  // Euros, because how it was typed was never recorded. Inventing a percentage from the basket it happened to land
  // on is the mistake §9.420 names in the other direction.
  if (loaded.discountCents > 0) return { mode: 'fixed', value: String(loaded.discountCents / 100) }
  return null
}

/**
 * The discount a save that says nothing about it leaves on the order, in cents - §9.744 amended, owner O114,
 * `BACKEND.md` §10A.397 amended.
 *
 * **The server's own rule, so the cart shows the figure the save will store.** A manual discount **in force** - a
 * type, a value above nought and a discount above nought - is worked out again on the goods: a percentage of them,
 * or the fixed amount, rounded to the cash step and capped at them, which is `manualDiscountCents`, the till's own
 * arithmetic and the server's. **Anything else** - a coupon, a figure from before the column, a definition left on the
 * row with no discount in force - is the stored figure exactly, capped at the goods.
 *
 * **Not re-rounded, and that is the point**: a legacy 2,34 EUR stays 2,34. Fed back through the box it came out as
 * 2,35, a figure the server neither stores nor accepts from a role without the discount right - 421 of Šurany's
 * orders and 106 of Nesvady's carry an untyped discount off the five-cent step.
 */
export function untouchedDiscountCents(loaded: LoadedOrder, subtotalCents: number): number {
  const inForce =
    loaded.manualDiscountType !== '' && loaded.manualDiscountValue > 0 && loaded.discountCents > 0
  if (inForce && loaded.manualDiscountType === 'percent') {
    return manualDiscountCents('percent', loaded.manualDiscountValue, subtotalCents)
  }
  if (inForce && loaded.manualDiscountType === 'fixed') {
    return manualDiscountCents('fixed', loaded.manualDiscountValue, subtotalCents)
  }
  return Math.min(loaded.discountCents, subtotalCents)
}

export function editScalars(loaded: LoadedOrder, current: EditFields): EditScalars {
  const scalars: EditScalars = {}
  const name = nameFor(loaded, current)

  if (current.type !== loaded.orderType) scalars.type = current.type
  if (name !== loaded.customerName) scalars.customerName = name
  if (current.customerPhone !== loaded.customerPhone) scalars.customerPhone = current.customerPhone
  if (current.customerEmail !== loaded.customerEmail) scalars.customerEmail = current.customerEmail
  if (current.customerAddress !== loaded.customerAddress)
    scalars.customerAddress = current.customerAddress
  /* §9.693, FB-1003 - **the point, beside the text it describes.** Only when it moved: the new point, or a
   * clear of both when the address changed without one. A typed-and-undone address restores the loaded point
   * in the builder (`pointForTypedAddress`), so it reaches here unmoved and sends nothing. */
  if (current.deliveryPoint !== undefined) {
    const moved = coordinatesPatch(current.deliveryPoint, loadedPointOf(loaded))
    if (moved !== null) {
      scalars.deliveryLat = moved.deliveryLat
      scalars.deliveryLon = moved.deliveryLon
    }
  }
  if (current.notes !== loaded.notes) scalars.notes = current.notes
  /* §9.420, FB-747 - the discount, which the save has never been able to carry.
   *
   * **Expressed exactly as `orderLinePayload`'s caller expresses it on create**: `discount` is the
   * whole figure in euros, and the three `manualDiscount*` fields are sent only where a manual
   * discount was actually given, because they record *who* gave it and how it was typed. Diverging
   * from the create path here is what FB-747's third report was, on a different field.
   *
   * The comparison is in **cents**, never euros: `discountCents` is an integer either side, so
   * there is no float to be almost-equal.
   *
   * §9.744 amended, owner O114 - **only a box somebody touched speaks.** Untouched - nobody typed in it, switched its
   * unit or cleared it, which is always so while it is hidden - the save sends no `discount` and no
   * `manualDiscount*`, and the server works the figure out from the lines (`BACKEND.md` §10A.397 amended; the cart
   * shows the same figure, `untouchedDiscountCents`). It used to send a figure of its own: a legacy 2,34 re-rounded to
   * 2,35, a coupon seeded as a fixed discount and capped at fewer goods, a cleared percentage re-seeded - and the
   * server read each as a new manual discount, so a role without the discount right was refused every save of such
   * an order. */
  if (!current.discountTouched) {
    /* The one thing an untouched box still owes the save: a coupon applied in this edit. This endpoint takes no coupon
       code, so the coupon is the figure alone - no definition beside it, which the server reads as the figure moving
       and asks either discount right for; the coupon box is drawn only for `apply_coupon`. */
    if (current.couponCents > 0 && current.discountCents !== loaded.discountCents) {
      scalars.discount = current.discountCents / 100
    }
  } else if (current.discountCents !== loaded.discountCents) {
    scalars.discount = current.discountCents / 100
    if (current.discountCents > 0 && current.discountValue.trim() !== '') {
      scalars.manualDiscountType = current.discountMode
      scalars.manualDiscountValue = Number(current.discountValue)
      scalars.manualDiscountOperator = current.operatorName
    } else {
      // Cleared. `present-and-null means clear it` is the server's own documented rule, so the
      // operator goes with the discount rather than outliving it - and, §9.744 amended, the type and the value
      // with them, as *Zaplatiť*'s clear sends it: left on the row, they were seeded back on the next edit.
      scalars.manualDiscountType = null
      scalars.manualDiscountValue = null
      scalars.manualDiscountOperator = null
    }
  }
  /* The delivery fee, for the same reason: it is in `EDITABLE`, the form resolves it, and until now
   * only `total` moved - which left the order's total and its own parts disagreeing. */
  if (current.deliveryCents !== loaded.deliveryCents) {
    scalars.deliveryCost = current.deliveryCents / 100
  }
  // §9.40 - a waiter only where a channel seats people. Sending one with a delivery would put the
  // takings against somebody who never touched the order.
  if (current.seatsGuests && current.waiterId !== null) scalars.employeeId = current.waiterId
  if (current.isPreorder !== loaded.isPreorder) scalars.isPreorder = current.isPreorder
  // A časenka's time, and `null` when it stops being one - which is what clears the column.
  if (current.isPreorder) {
    const scheduled = current.scheduledAt ?? ''
    if (scheduled !== '' && !sameMinute(scheduled, loaded.scheduledAt)) {
      scalars.scheduledAt = scheduled
    }
  } else if (loaded.isPreorder) {
    scalars.scheduledAt = null
  }
  // §9.374 - refused only where the money arrived through the method it names.
  if (current.paymentMethod !== loaded.paymentMethod && mayChangePaymentMethod(loaded)) {
    scalars.paymentMethod = current.paymentMethod
    // The board draws *Platba čaká* off `card_online` **or** a pending status, so leaving the old
    // status behind would keep the red badge on an order now paid at the door.
    if (loaded.paymentStatus !== '') scalars.paymentStatus = null
  }
  return scalars
}

export function editPayload(
  cart: readonly (OrderCartLine & { existingId?: number })[],
  loaded?: LoadedOrder | null,
  scalars: EditScalars = {},
  /**
   * The payable figure the cart shows, in cents - §9.420.
   *
   * **Optional, defaulting to the goods**, so every existing caller and test keeps its exact
   * meaning while the one caller that has the real figure passes it. Without it this function would
   * have to recompute a total it cannot compute: it has the lines and knows nothing of the zone's
   * fee, the deposits or the discount.
   */
  totalCents = cart
    .filter((line) => line.quantity > 0)
    .reduce((sum, line) => sum + parseMoneyToCents(line.unitPrice) * line.quantity, 0),
): Record<string, unknown> | null {
  const added = cart.filter((line) => line.existingId === undefined)
  const live = cart.filter((line) => line.quantity > 0)

  // **Did anything that was already on the order move?** A quantity stepped down, a line removed, a
  // line that is no longer in the cart at all. Only `items` can express any of the three.
  const before = new Map<number, number>()
  for (const line of loaded?.lines ?? []) {
    const id = (line as OrderCartLine & { existingId?: number }).existingId
    if (id !== undefined) before.set(id, line.quantity)
  }
  const now = new Map<number, number>()
  for (const line of live) {
    if (line.existingId !== undefined) now.set(line.existingId, line.quantity)
  }
  /* §9.713 - **a new add-on of a line already on the order can only go as `items`.** `newItems` names a parent by
     its index in `newItems` (`BACKEND.md` §10A.366), which a line the order already holds has none of. Unreachable
     from the prompt today - it opens after an add, and a "+" on a loaded line changes its quantity, which takes this
     path anyway - so this keeps the link rather than inventing a second way to say it. */
  const addedIds = new Set(added.map((line) => line.lineId).filter((id) => id !== undefined))
  const addonOfAnExistingLine = added.some(
    (line) => line.addonOf !== undefined && !addedIds.has(line.addonOf) && live.some((other) => other.lineId === line.addonOf),
  )
  const existingChanged =
    (before.size > 0 &&
      [...before.entries()].some(([id, quantity]) => (now.get(id) ?? 0) !== quantity)) ||
    // §9.730 - a saved pizza whose toppings or base were edited: the quantity is the same and the line is not.
    renamedExistingIds(live, loaded).size > 0 ||
    addonOfAnExistingLine

  const scalarKeys = Object.keys(scalars) as (keyof EditScalars)[]
  const hasScalars = scalarKeys.some((key) => scalars[key] !== undefined)

  if (existingChanged) {
    // §9.713 - each add-on names its parent by its index in **this** array.
    const items = withParentLinks(live, live.map(itemOf))
    /* §9.420, FB-747 - **the figure the screen shows, not a second sum of the goods.**
     *
     * This read `live.reduce(unitPrice * quantity)`, which is the subtotal: no delivery, no deposits,
     * no discount. `total` is in the server's `EDITABLE`, so an edited delivery order had the goods
     * figure written over its real total while `delivery_cost` kept the fee - the order's total then
     * disagreeing with its own parts. That is the owner's 17,00 €.
     *
     * `totalCents` comes from `cartTotals`, the one function that already decides this figure for the
     * cart, the receipt preview and the confirmation. §10A.138 is what it costs when a figure has
     * several readers and one of them recomputes it, and this was that. */
    return { items, total: totalCents / 100, ...scalars }
  }
  /* §9.690, `BACKEND.md` §10A.349 - **a note-only change is not a replace.** Sent as `lineNotes`, one
   * entry per changed line by its own id, so the server writes the note column and nothing else: no
   * line is re-inserted, nothing is re-priced, and an order whose receipt is printed is not asked
   * whether to cancel it over a note. Only when nothing above moved - a quantity change already sends
   * every note inside `items`. */
  const changedNotes = lineNoteChanges(cart, loaded)
  const lineNotes = changedNotes.length > 0 ? { lineNotes: changedNotes } : {}
  if (added.length > 0) {
    // §9.713 - a round's add-ons name their parent by its index in the round.
    return { newItems: withParentLinks(added, added.map(itemOf)), ...lineNotes, ...scalars }
  }
  if (changedNotes.length > 0) {
    return { ...lineNotes, ...scalars }
  }
  return hasScalars ? { ...scalars } : null
}


/**
 * Whether this order's payment method may still be changed - §9.374, FB-668.
 *
 * **The owner's case is the *failed* one**: *"when objednávka cardpay neprešla, we cant edit it and
 * change it to karta kuriér."* An online payment that never landed has no money against it, so the
 * method is still a question. One that **did** land is not: `online_paid` means the customer's card
 * was charged through CardPay, and relabelling it *Karta kuriér* would put that money in the
 * courier's column and short the handover by exactly the amount.
 *
 * Nothing else is refused here. A cash order changed to card, or the other way, is an ordinary
 * correction; the fiscal guard on the server (`OrderStatusService`'s `lockedPaymentMethod`) is what
 * stops a rewrite after a receipt is printed, and it is not this screen's rule to duplicate.
 */
export function mayChangePaymentMethod(order: LoadedOrder | null): boolean {
  return order === null || order.paymentStatus !== 'online_paid'
}

/** Said next to the selector when the money has already arrived through the method it names. */
export function paymentMethodLockedNote(): string {
  return tt(
    'Online platba už prešla - spôsob platby sa nedá zmeniť, inak by tržba sedela na inom stĺpci.',
  )
}

/**
 * What is about to happen, named before the button is pressed - §9.374.
 *
 * **It used to be able to say only *nothing was added*.** With three branches behind the save, the
 * sentence has to tell a correction from a round from a note: the operator who read *"Zatiaľ nič
 * nepridané"* after decrementing a drink was told the opposite of what they had just done.
 */
export function editSummary(
  cart: readonly (OrderCartLine & { existingId?: number })[],
  loaded?: LoadedOrder | null,
  scalars: EditScalars = {},
): string {
  const added = cart.filter((line) => line.existingId === undefined)
  const live = cart.filter((line) => line.quantity > 0)

  const before = new Map<number, number>()
  for (const line of loaded?.lines ?? []) {
    if (line.existingId !== undefined) before.set(line.existingId, line.quantity)
  }
  const now = new Map<number, number>()
  for (const line of live) {
    if (line.existingId !== undefined) now.set(line.existingId, line.quantity)
  }
  // §9.730 - a saved pizza with its toppings or base changed is a correction too, counted once with its quantity.
  const corrected = new Set([
    ...[...before.entries()].filter(([id, was]) => (now.get(id) ?? 0) !== was).map(([id]) => id),
    ...renamedExistingIds(live, loaded),
  ]).size

  const parts: string[] = []
  if (added.length > 0) {
    const total = added.reduce(
      (sum, line) => sum + parseMoneyToCents(line.unitPrice) * line.quantity,
      0,
    )
    parts.push(
      `${tt('Pridá sa')} ${added.map((line) => `${line.quantity}× ${line.productName}`).join(', ')} - ${formatCents(total)}`,
    )
  }
  if (corrected > 0) {
    // Said out loud, because it is the one branch that rewrites the order rather than extending it:
    // the kitchen gets no new round for a correction, which is legacy's behaviour and a surprise
    // worth naming on the screen.
    parts.push(`${tt('Opraví sa už zadaných položiek')}: ${corrected} - ${tt('bez nového kola')}`)
  }
  if (scalars.notes !== undefined) parts.push(tt('Zmení sa poznámka'))
  // §9.690 - a note on a line is a change the save makes, and the sentence used to say there was none.
  if (lineNoteChanges(cart, loaded).length > 0) parts.push(tt('Zmení sa poznámka k položke'))
  if (scalars.paymentMethod !== undefined) parts.push(tt('Zmení sa spôsob platby'))
  if (parts.length === 0) return tt('Zatiaľ nič nezmenené - objednávka zostane, ako je.')
  return parts.join(' · ')
}

/**
 * Whether this order may be edited at all.
 *
 * `PATCH /orders/{orderId}`'s own summary: *"Edit an order — anything but the three statuses that settle
 * money."* Asking on a settled order and being refused is a worse answer than not offering it.
 */
export const SETTLED = ['completed', 'delivered', 'cancelled']

export function mayEdit(order: LoadedOrder): boolean {
  return editRefusal(order) === null
}

/**
 * What a live fiscal document means for this edit - §9.620, owner R2.
 *
 * <p>**This was a refusal and the owner asked for a question.** §9.567 returned it from
 * `editRefusal`, so `blockedReason()` stopped the save and the *Úprava objednávky* box printed it in
 * red: *"Najprv doklad stornujte, potom ju upravte."* The owner's words are *"miesto toho, ze ti
 * nepovoli ulozit a musis najprv storno, sa ta opyta po kliknuti na ulozenie"*, so the screen now
 * says what will happen and the question comes at save time.
 *
 * <p>**It no longer promises which edits will be asked about, and that is deliberate.** The server
 * owns `MAY_EDIT_AFTER_PRINT` - eleven names, from `notes` to the driver - and §9.567's refusal
 * blocked every one of them, so correcting a delivery address on an order already rung up could not
 * be saved. A client-side copy of that list to word this sentence more precisely would be
 * `agos-one-guard-spelled-twice` on a tax allow-list whose own javadoc says an unnamed field must be
 * refused; the honest sentence is *if it moves the figures, you will be asked*.
 */
export function printedNote(): string {
  return `${tt('Objednávka má vytlačený fiškálny doklad.')} ${tt('Ak úprava zmení sumu, pri ukladaní sa spýtame, či doklad stornovať.')}`
}

/**
 * That warning where there is a document to warn about, or `''` - §9.620.
 *
 * <p>`lockedNote`'s own shape, six lines above, so the screen renders the two the same way and the
 * `hasLiveFiskalDocument` read stays in this file with the rest of the edit rules rather than being
 * imported into a 2 000-line component.
 */
export function printedWarning(order: LoadedOrder): string {
  return hasLiveFiskalDocument(order) ? printedNote() : ''
}

/**
 * Why this order may not be edited **at all**, or null - §9.567, FB-862, narrowed by §9.620.
 *
 * <p>**One function because there are two reasons now.** `mayEdit` answered a boolean and
 * `settledNote` explained it, and both call sites paired them by hand; a second reason with its own
 * note would have been a second pair to remember. This answers the sentence, `mayEdit` delegates, and
 * the two cannot disagree.
 *
 * <p>**The live document is no longer one of the reasons** - owner R2. It is a warning now
 * ({@link printedNote}) and the decision is taken on the server's own refusal, which is what stops
 * this screen re-deriving a rule it cannot see. What is left is genuinely not editable: a settled
 * order's money is reconciled, and *a further order is a new one* is the answer whatever it carries.
 */
export function editRefusal(order: LoadedOrder): string | null {
  if (SETTLED.includes(order.status)) return settledNote(order)
  return null
}

/**
 * Whether the server refused this save for want of a decision about a live fiscal document - §9.620.
 *
 * <p>**The code, never the sentence.** §10A.315 answers `FISKAL_DOCUMENT_LIVE` on that one 400
 * precisely so this can be asked without reading Slovak: the same save can answer §10A.300's unknown
 * status, §10A.282's *paymentStatus sa dá iba zmazať* and §10A.286's channel rule, and matching the
 * message for *stornujte* is §9.610's defect - behaviour keyed on the words of a translated string,
 * which a rewording changes with nothing anywhere to say so.
 *
 * <p>Through `apiErrorField` rather than `ApiError.code` so a hand-built failure in a test is read
 * the same way as a real one - that helper's own reason, and it keeps this module off `isApiError`.
 */
export function needsAFiskalDecision(error: unknown): boolean {
  return apiErrorField(error, 'code') === 'FISKAL_DOCUMENT_LIVE'
}

export function settledNote(order: LoadedOrder): string {
  return `${tt('Objednávka je uzavretá')} (${order.status}) - ${tt('upraviť sa už nedá; peniaze sú vyrovnané. Doobjednávka je nová objednávka.')}`
}

/**
 * Whether the screen may write at all, when it was asked to edit an order - §9.432.
 *
 * **The owner's report:** *"chcela dohodiť nejaké veci do nej … vytvorilo druhu obj kde bola len
 * mirinda"*. `NewOrderPage` branched on `editing !== null` - the **fetched** order - so until that
 * request landed, or if it failed, the save fell through to `createOrder` and made a second order
 * for the same table. The cart was empty at the same moment, because the seeding is gated on the
 * same value, so the new order held only what the operator had just typed.
 *
 * **Refusing is the whole fix.** There is nothing safe to guess: the cart holds what was typed and
 * nothing of the order, so any write is either a new order or a patch that deletes the lines the
 * kitchen is cooking from.
 *
 * **Two states, two sentences.** *Still coming* and *could not be fetched* need different words - the
 * first asks for a moment's patience, the second for another attempt - and a screen that says neither
 * is what sent the operator down this path: it looked ready and the cart looked empty.
 *
 * @param editId the `edit=` parameter, or null when taking a new order
 * @param loaded what came back, or null while it has not
 */
export function editLoadProblem(
  editId: number | null,
  loaded: LoadedOrder | null,
  state: { failed: boolean },
): string | null {
  if (editId === null || loaded !== null) {
    return null
  }
  return state.failed
    ? `${tt('Objednávku sa nepodarilo načítať')} (#${editId}). ${tt('Skúste to znova.')}`
    : `${tt('Objednávka sa ešte načítava')} (#${editId}).`
}

/**
 * The lines the server has that this cart was never given - §9.433, FB-756.
 *
 * **The owner:** *"I edited it and add nápoj, saved. Napoj was there. I edited it and added another
 * šunková pizza, and then nápoj disappeared."*
 *
 * <p><b>Why a merge and not a re-seed.</b> `NewOrderPage` pours the loaded order into the form once,
 * keyed on the order's id, because re-running it on every render would undo whatever the person
 * editing had just typed. That is right, and it means the cart cannot take a line the order gains
 * *after* the first pour - which is exactly what happened: the save invalidated the order **list**
 * and not the order, so the second edit seeded from the pre-edit-1 detail and the refetch that
 * brought the drink could not re-seed.
 *
 * <p><b>What makes it data loss rather than a stale screen.</b> `editPayload` reads a loaded id the
 * cart does not hold as `now.get(id) ?? 0` - quantity nought, *the operator removed this line* - and
 * a removal can only be expressed by replacing the whole list. So the missing line was sent as
 * deleted. **A line the cart was never given is indistinguishable from one the operator deleted**,
 * and that ambiguity is the defect rather than any single wrong line.
 *
 * <p>Which is why the fix cannot be *invalidate the right key* alone. That closes this sequence and
 * leaves the shape: a poll, a refetch on window focus, or a second till editing the same table all
 * arrive the same way. The cart records what it has ever been seeded with, so:
 *
 * <ul>
 *   <li>a line the server has and the cart was <b>never seeded with</b> is new information - append it;
 *   <li>a line that <b>was</b> seeded and is now absent is a removal - leave it absent.
 * </ul>
 *
 * After this, `?? 0` can only mean *removed*.
 *
 * @param loaded the order as the server last described it
 * @param seeded every `existingId` this cart has already been given, across every refetch
 */
export function unseededServerLines(
  loaded: LoadedOrder | null | undefined,
  seeded: ReadonlySet<number>,
): (OrderCartLine & { existingId?: number })[] {
  if (!loaded) return []
  return loaded.lines.filter((line) => {
    const id = (line as OrderCartLine & { existingId?: number }).existingId
    // A line with no id cannot be tracked and cannot have come from the server as an existing row;
    // appending it would duplicate whatever the operator is typing.
    return id !== undefined && !seeded.has(id)
  })
}

/** Every `existingId` in a set of lines, for recording what a cart has been seeded with. */
export function existingIdsOf(
  lines: readonly (OrderCartLine & { existingId?: number })[],
): number[] {
  return lines
    .map((line) => line.existingId)
    .filter((id): id is number => typeof id === 'number')
}
